import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { ServiceItem } from '@/api/types'
import { serviceCardTargetFromItem } from '@/api/servicePageResolver'
import AuditCtaButton from '@/components/ui/AuditCtaButton'
import { cmsTextOrFallback } from '@/api/helpers'
import { cmsClamp, cmsParagraph } from '@/styles/cmsText'

import './Services.css'

const DESKTOP_CARD_WIDTH = 402
const DESKTOP_CARD_GAP = 16
const DESKTOP_CARDS_VISIBLE = 4
const DESKTOP_VIEWPORT_WIDTH =
  DESKTOP_CARD_WIDTH * DESKTOP_CARDS_VISIBLE + DESKTOP_CARD_GAP * (DESKTOP_CARDS_VISIBLE - 1)
const DESKTOP_CARD_STEP = DESKTOP_CARD_WIDTH + DESKTOP_CARD_GAP
const SLIDER_AUTOPLAY_MS = 4000
const SLIDER_NAV_SIZE = 52
const SLIDER_NAV_GAP = 24
const SLIDER_ROW_WIDTH =
  DESKTOP_VIEWPORT_WIDTH + (SLIDER_NAV_SIZE + SLIDER_NAV_GAP) * 2



const DEFAULT_SERVICES_TITLE = 'What We Offer'
const DEFAULT_SERVICES_DESCRIPTION =
  'Explore our core capabilities designed to help your organization grow with clarity and confidence.'

type ServicesProps = {
  isMobile: boolean
  cmsReady?: boolean
  title?: string
  description?: string
  items?: ServiceItem[]
}

const SECTION_CTA = 'Get your Free Audit'

export default function Services({
  isMobile,
  cmsReady = true,
  title,
  description,
  items,
}: ServicesProps) {
  const sectionTitle = cmsTextOrFallback(title, DEFAULT_SERVICES_TITLE, cmsReady)
  const sectionDescription = cmsTextOrFallback(description, DEFAULT_SERVICES_DESCRIPTION, cmsReady)
  const cardItems =
    !cmsReady && !items?.length
      ? []
      : (items ?? []).filter((item) => item.title?.trim() || item.description?.trim())

  if (isMobile) {

    return (

      <section

        id="services"

        className="relative overflow-hidden bg-white py-16"

        aria-label="Our services"

      >

        <div className="mx-auto px-4 services-section__header services-section__header--mobile" style={{ maxWidth: '428px', width: '100%' }}>

          <h2
            className="text-2xl font-semibold leading-tight text-gray-900"
            title={sectionTitle}
            style={{
              width: '100%',
              textAlign: 'center',
              margin: 0,
              ...cmsClamp(2),
            }}
          >
            {sectionTitle}
          </h2>

          <p
            className="mt-4 text-lg leading-relaxed text-gray-600"
            title={sectionDescription}
            style={{
              width: '100%',
              textAlign: 'center',
              margin: 0,
              ...cmsParagraph(),
            }}
          >
            {sectionDescription}
          </p>

          <div className="mt-8 flex flex-col gap-4 sm:gap-6">

            {cardItems.map((item) => (

              <ServiceCard 

                key={`svc-${item.id}-${item.title}`} 

                item={item} 

                isMobile={true}

              />

            ))}

          </div>

          <div className="mt-16 text-center">

            <AuditCtaButton href="#contact" variant="compact">
              {SECTION_CTA}
            </AuditCtaButton>

          </div>

        </div>

      </section>

    )

  }



  return (

    <section
      id="services"
      className="services-section services-section--desktop relative overflow-visible bg-white pt-12 pb-24"
      aria-label="Our services"
    >

      <div className="relative mx-auto px-4" style={{ maxWidth: '104rem' }}>

        <div className="services-section__header">

          <h2 title={sectionTitle} className="services-section__title">
            {sectionTitle}
          </h2>

          <p title={sectionDescription} className="services-section__description">
            {sectionDescription}
          </p>

        </div>


        <ServicesCardsSlider items={cardItems} />



        <div className="text-center mt-20">

          <AuditCtaButton href="#contact" variant="default">
            {SECTION_CTA}
          </AuditCtaButton>

        </div>

      </div>

    </section>

  )

}

function ServicesCardsSlider({ items }: { items: ServiceItem[] }) {
  const maxIndex = Math.max(0, items.length - DESKTOP_CARDS_VISIBLE)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const pauseUntilRef = useRef(0)
  const canSlide = items.length > DESKTOP_CARDS_VISIBLE

  useEffect(() => {
    setActiveIndex((prev) => Math.min(prev, maxIndex))
  }, [maxIndex])

  const pauseAutoplayBriefly = useCallback(() => {
    pauseUntilRef.current = Date.now() + SLIDER_AUTOPLAY_MS * 2
    setIsPaused(true)
  }, [])

  const goPrev = useCallback(() => {
    pauseAutoplayBriefly()
    setActiveIndex((prev) => (prev <= 0 ? maxIndex : prev - 1))
  }, [maxIndex, pauseAutoplayBriefly])

  const goNext = useCallback(() => {
    pauseAutoplayBriefly()
    setActiveIndex((prev) => (prev >= maxIndex ? 0 : prev + 1))
  }, [maxIndex, pauseAutoplayBriefly])

  const goTo = useCallback(
    (index: number) => {
      pauseAutoplayBriefly()
      setActiveIndex(Math.min(maxIndex, Math.max(0, index)))
    },
    [maxIndex, pauseAutoplayBriefly]
  )

  useEffect(() => {
    if (!canSlide) return

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) return

    const tick = () => {
      if (isPaused) {
        if (Date.now() >= pauseUntilRef.current) {
          setIsPaused(false)
        }
        return
      }
      setActiveIndex((prev) => (prev >= maxIndex ? 0 : prev + 1))
    }

    const id = window.setInterval(tick, SLIDER_AUTOPLAY_MS)
    return () => window.clearInterval(id)
  }, [canSlide, isPaused, maxIndex])

  if (!items.length) return null

  const showNav = items.length > 1

  return (
    <div
      className="services-slider mx-auto mb-12"
      style={{ maxWidth: `${SLIDER_ROW_WIDTH}px` }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => {
        pauseUntilRef.current = 0
        setIsPaused(false)
      }}
      onFocusCapture={() => setIsPaused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          pauseUntilRef.current = 0
          setIsPaused(false)
        }
      }}
    >
      <div className="services-slider__main">
        <div className="services-slider__row">
          {showNav ? (
            <button
              type="button"
              className="services-slider__nav services-slider__nav--prev"
              onClick={goPrev}
              aria-label="Previous services"
            >
              <ChevronLeft size={28} strokeWidth={2} aria-hidden />
            </button>
          ) : null}

          <div
            className="services-slider__viewport"
            style={{ width: `${DESKTOP_VIEWPORT_WIDTH}px` }}
            aria-roledescription="carousel"
            aria-label="Our services"
            aria-live="polite"
          >
            <div
              className="services-slider__track"
              style={{ transform: `translate3d(-${activeIndex * DESKTOP_CARD_STEP}px, 0, 0)` }}
            >
              {items.map((item) => (
              <ServiceCard
                key={`svc-${item.id}-${item.title}`}
                item={item}
                isMobile={false}
                className="services-slider__card"
              />
              ))}
            </div>
          </div>

          {showNav ? (
            <button
              type="button"
              className="services-slider__nav services-slider__nav--next"
              onClick={goNext}
              aria-label="Next services"
            >
              <ChevronRight size={28} strokeWidth={2} aria-hidden />
            </button>
          ) : null}
        </div>

        {canSlide ? (
          <div className="services-slider__dots" role="tablist" aria-label="Service slides">
            {Array.from({ length: maxIndex + 1 }, (_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                className={`services-slider__dot${i === activeIndex ? ' services-slider__dot--active' : ''}`}
                aria-label={`Go to slide ${i + 1} of ${maxIndex + 1}`}
                aria-selected={i === activeIndex}
                onClick={() => goTo(i)}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}

/** Split "Odoo ERP: Get Control…" → line 1 "Odoo ERP:", line 2 "Get Control…" */
function splitServiceCardTitle(title: string): { headline: string; subtitle: string } | null {
  const match = title.match(/^([^:]+:)\s+(.+)$/)
  if (!match) return null
  return { headline: match[1], subtitle: match[2] }
}

function ServiceCardTitle({ title, isMobile }: { title: string; isMobile?: boolean }) {
  const split = splitServiceCardTitle(title)

  return (
    <h3
      className={`service-card-title ${isMobile ? 'service-card-title--mobile' : 'service-card-title--desktop'}`}
      title={title}
    >
      {split ? (
        <>
          <span className="service-card-title__line">{split.headline}</span>
          <span className="service-card-title__line">{split.subtitle}</span>
        </>
      ) : (
        title
      )}
    </h3>
  )
}

function ServiceCardLearnMoreContent() {
  return (
    <>
      <span className="service-card-learn-more__label">Learn more</span>
      <svg
        className="service-card-learn-more__chevron"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="m9 18 6-6-6-6" />
      </svg>
    </>
  )
}

function ServiceCard({
  item,
  isMobile,
  className = '',
}: {
  item: ServiceItem
  isMobile?: boolean
  className?: string
}) {
  const iconSrc = item.icon?.trim() ?? ''
  const cardTitle = item?.title?.trim() || 'Service'
  const cardTarget = serviceCardTargetFromItem(item)
  const learnMoreClassName = `service-card-learn-more ${isMobile ? '' : 'service-card-learn-more--desktop'}`
  const rawDescription = (item?.description ?? '').trim()
  const cardDescription = rawDescription || 'Description coming soon.'

  const handleCardMouseEnter = (e: MouseEvent<HTMLElement>) => {
    e.currentTarget.style.background = '#3D94D0'
    const h3Element = e.currentTarget.querySelector('h3') as HTMLElement
    const pElement = e.currentTarget.querySelector('p') as HTMLElement
    if (h3Element) h3Element.style.color = '#FFFFFF'
    if (pElement) pElement.style.color = '#FFFFFF'
  }

  const handleCardMouseLeave = (e: MouseEvent<HTMLElement>) => {
    e.currentTarget.style.background = '#E2EFF8'
    const h3Element = e.currentTarget.querySelector('h3') as HTMLElement
    const pElement = e.currentTarget.querySelector('p') as HTMLElement
    if (h3Element) h3Element.style.color = '#141218'
    if (pElement) pElement.style.color = '#484646'
  }

  const cardBody = (
    <>
      <div className="service-card__icon">
        {iconSrc ? (
          <img
            src={iconSrc}
            alt=""
            className="w-full h-full object-contain"
            style={{ filter: 'brightness(0) invert(0)' }}
            loading="lazy"
          />
        ) : (
          <span className="service-card-icon-fallback text-2xl" style={{ color: '#141218' }} aria-hidden>
            ◆
          </span>
        )}
      </div>

      <ServiceCardTitle title={cardTitle} isMobile={isMobile} />

      <p
        className={`service-card-desc ${isMobile ? 'service-card-desc--mobile' : 'service-card-desc--desktop'}`}
        title={cardDescription}
      >
        {cardDescription}
      </p>

      {cardTarget?.type === 'internal' ? (
        <Link to={cardTarget.to} className={learnMoreClassName}>
          <ServiceCardLearnMoreContent />
        </Link>
      ) : cardTarget?.type === 'external' ? (
        <a
          href={cardTarget.href}
          className={learnMoreClassName}
          {...(cardTarget.href.startsWith('http')
            ? { target: '_blank', rel: 'noopener noreferrer' }
            : {})}
        >
          <ServiceCardLearnMoreContent />
        </a>
      ) : (
        <a href="#contact" className={learnMoreClassName}>
          <ServiceCardLearnMoreContent />
        </a>
      )}
    </>
  )

  return (
    <article
      className={`group transition-all duration-300 ${isMobile ? `service-card service-card--mobile flex flex-col items-start text-left w-full ${className}` : `service-card service-card--desktop ${className}`}`.trim()}
      onMouseEnter={handleCardMouseEnter}
      onMouseLeave={handleCardMouseLeave}
    >
      {cardBody}
    </article>
  )

}

